import Button from '@/common/components/Button'
import Select from '@/common/components/Select'
import { controlStyles } from '@/common/components/Select/styles'
import { useCopyButton } from '@/common/hooks/useCopyButton'
import { Option } from '@/common/types/option'
import { useInfocarRequestCache } from '@/features/modelIntegrationCodes/hooks/useInfocarRequestCache'
import { useMemo } from 'react'

interface InfocarInfoProps {
  modelIntegrationCodeId?: string
  equipments: Option[]
  selectedOption?: Option
  onEquipmentChange: (equipment: Option) => void
  isLoadingEquipments?: boolean
  onEquipmentSearch?: (search: string) => void
}

const InfocarInfo = ({
  modelIntegrationCodeId,
  equipments,
  selectedOption,
  onEquipmentChange,
  isLoadingEquipments,
  onEquipmentSearch,
}: InfocarInfoProps) => {
  const { control: copyControl, copySuccess, copyFailed } = useCopyButton()
  const CopyIcon = copyControl.icon

  const { data, isPending, error } = useInfocarRequestCache({
    modelIntegrationCodeId,
    equipmentId: selectedOption?.value,
  })

  const formattedData = useMemo(() => {
    if (!data?.response) return ''
    return JSON.stringify(data.response, null, 2)
  }, [data])

  const handleCopy = () => {
    if (!formattedData) return
    try {
      copySuccess(formattedData)
    } catch {
      copyFailed()
    }
  }

  const placeholder = !selectedOption?.value
    ? 'Selecione um equipamento para ver as informações da Infocar.'
    : isPending
      ? 'Carregando informações da Infocar...'
      : error
        ? 'Não foi possível carregar as informações da Infocar.'
        : '--'

  return (
    <div className="flex-1 min-h-0 min-w-0 flex flex-col rounded-sm overflow-hidden">
      <div className="flex items-center justify-between gap-2 p-2 bg-white-100 shrink-0">
        <p className="text-xs whitespace-nowrap">Informações da Infocar</p>

        <div className="flex items-center gap-2">
          <Select
            className="min-w-0 max-w-xs"
            customStyles={{
              ...controlStyles,
              control: () => 'px-2 min-h-8 max-h-8 bg-neutral-op-8 outline-none border-none',
              indicatorsContainer: () => 'h-8',
            }}
            placeholder="Equipamento"
            options={equipments}
            value={selectedOption}
            onChange={(option: any) => option?.value && onEquipmentChange(option)}
            onInputChange={(input) => onEquipmentSearch?.(input)}
            isLoading={isLoadingEquipments}
            isDisabled={!modelIntegrationCodeId}
            dataTestid="equipment_selector"
            operationKlassToCheckPermission="Admin::EquipmentModels::EquipmentsFilter"
          />

          <Button
            name="copy_infocar_info"
            className="bg-white-100 hover:bg-white-200 text-xs italic font-light px-4 py-2 rounded-lg gap-2 h-auto"
            onClick={handleCopy}
            disabled={!formattedData}
          >
            {copyControl.message}
            <CopyIcon size={16} />
          </Button>
        </div>
      </div>

      <pre className="flex-1 min-h-0 bg-neutral-8 px-2 py-2 text-xs font-mono whitespace-pre overflow-auto">
        {formattedData || placeholder}
      </pre>
    </div>
  )
}

export default InfocarInfo
